// HtmlFormElements.jsx
import React from "react";

const HtmlFormElements = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Form Elements - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML forms use a variety of elements to collect user input. Understanding the purpose
          and usage of each element ensures better user experience and data handling.
        </p>
      </section>

      {/* Text Input */}
      <section id="text-input">
        <h2>2. Text Input</h2>
        <p>Single-line input fields for text data.</p>
        <pre>
          <code>
{`<form>
  <label htmlFor="name">Name:</label>
  <input type="text" id="name" name="name" placeholder="Enter your name" required />
</form>`}
          </code>
        </pre>
      </section>

      {/* Password Input */}
      <section id="password-input">
        <h2>3. Password Input</h2>
        <p>Input fields that hide the entered text.</p>
        <pre>
          <code>
{`<label htmlFor="password">Password:</label>
<input type="password" id="password" name="password" required />`}
          </code>
        </pre>
      </section>

      {/* Email Input */}
      <section id="email-input">
        <h2>4. Email Input</h2>
        <p>Input field for email addresses with built-in validation.</p>
        <pre>
          <code>
{`<label htmlFor="email">Email:</label>
<input type="email" id="email" name="email" required />`}
          </code>
        </pre>
      </section>

      {/* Number Input */}
      <section id="number-input">
        <h2>5. Number Input</h2>
        <p>Input field for numeric data with optional min, max, and step attributes.</p>
        <pre>
          <code>
{`<label htmlFor="age">Age:</label>
<input type="number" id="age" name="age" min="1" max="100" step="1" />`}
          </code>
        </pre>
      </section>

      {/* Textarea */}
      <section id="textarea">
        <h2>6. Textarea</h2>
        <p>Multi-line text input for longer content.</p>
        <pre>
          <code>
{`<label htmlFor="message">Message:</label>
<textarea id="message" name="message" rows="5" cols="30" placeholder="Enter your message"></textarea>`}
          </code>
        </pre>
      </section>

      {/* Select / Dropdown */}
      <section id="select">
        <h2>7. Select (Dropdown)</h2>
        <p>Allows users to select one or more options from a list.</p>
        <pre>
          <code>
{`<label htmlFor="country">Country:</label>
<select id="country" name="country">
  <option value="">Select a country</option>
  <option value="us">United States</option>
  <option value="uk">United Kingdom</option>
  <option value="ca">Canada</option>
</select>`}
          </code>
        </pre>
      </section>

      {/* Radio Buttons */}
      <section id="radio">
        <h2>8. Radio Buttons</h2>
        <p>Allows selection of only one option from a set.</p>
        <pre>
          <code>
{`<p>Gender:</p>
<input type="radio" id="male" name="gender" value="male" />
<label htmlFor="male">Male</label>

<input type="radio" id="female" name="gender" value="female" />
<label htmlFor="female">Female</label>`}
          </code>
        </pre>
      </section>

      {/* Checkboxes */}
      <section id="checkbox">
        <h2>9. Checkboxes</h2>
        <p>Allows multiple selections from a set of options.</p>
        <pre>
          <code>
{`<p>Hobbies:</p>
<input type="checkbox" id="reading" name="hobbies" value="reading" />
<label htmlFor="reading">Reading</label>

<input type="checkbox" id="sports" name="hobbies" value="sports" />
<label htmlFor="sports">Sports</label>`}
          </code>
        </pre>
      </section>

      {/* File Input */}
      <section id="file-input">
        <h2>10. File Input</h2>
        <p>Allows users to upload files.</p>
        <pre>
          <code>
{`<label htmlFor="resume">Upload Resume:</label>
<input type="file" id="resume" name="resume" />`}
          </code>
        </pre>
      </section>

      {/* Submit Button */}
      <section id="submit-button">
        <h2>11. Submit Button</h2>
        <p>Sends the form data to the server.</p>
        <pre>
          <code>
{`<input type="submit" value="Submit" />`}
          </code>
        </pre>
      </section>

      {/* Reset Button */}
      <section id="reset-button">
        <h2>12. Reset Button</h2>
        <p>Clears all form inputs.</p>
        <pre>
          <code>
{`<input type="reset" value="Reset" />`}
          </code>
        </pre>
      </section>

      {/* Hidden Input */}
      <section id="hidden-input">
        <h2>13. Hidden Input</h2>
        <p>Stores data that is not visible to users.</p>
        <pre>
          <code>
{`<input type="hidden" name="token" value="12345" />`}
          </code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>14. Best Practices</h2>
        <ul>
          <li>Use <strong>label</strong> elements for accessibility.</li>
          <li>Use the correct input type for validation and mobile-friendly input.</li>
          <li>Group related inputs using <strong>fieldset</strong> and <strong>legend</strong>.</li>
          <li>Provide placeholders or help text for clarity.</li>
          <li>Use <strong>required</strong> and other validation attributes wisely.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlFormElements;
